import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from "@mui/icons-material";
import { useProductGroups } from "./useProductGroups";
import { useProducts } from "../products/useProducts";
import groupBy from "lodash/groupBy";
import type { FC } from "react";
import { useState } from "react";
import AsyncLoadingButton from "../../components/asyncLoadingButton/AsyncLoadingButton";
import type { ProductGroup } from "./productGroupsApi";
import CancelButton from "../../components/cancelButton/CancelButton";
import DictionaryAccordion from "./DictionaryAccordion";
import UsedByProducts from "./UsedByProducts";
import ConfirmationDialog from "../../components/confirmationDialog/ConfirmationDialog";

interface AddGroupFooterProps {
  readonly onAdd: (name: string) => Promise<void>;
}

const AddGroupFooter: FC<AddGroupFooterProps> = ({ onAdd }) => {
  const [name, setName] = useState("");
  return (
    <TableBody>
      <TableRow>
        <TableCell />
        <TableCell>
          <TextField label="Nazwa" fullWidth value={name} onChange={(e) => setName(e.target.value)} />
        </TableCell>
        <TableCell />
        <TableCell>
          <AsyncLoadingButton
            disabled={!name}
            onClick={async () => {
              await onAdd(name);
              setName("");
            }}
          >
            Dodaj
          </AsyncLoadingButton>
        </TableCell>
      </TableRow>
    </TableBody>
  );
};

interface EditGroupDialogProps {
  readonly open: boolean;
  readonly group: Pick<ProductGroup, "name">;
  readonly onChange: (name: string) => Promise<void>;
  readonly onCancel: () => void;
}

const EditGroupDialog: FC<EditGroupDialogProps> = ({ open, group, onChange, onCancel }) => {
  const [name, setName] = useState(() => group.name ?? "");

  const handleSave = async () => {
    await onChange(name);
  };
  const saveDisabled = !name || name === group.name;

  return (
    <Dialog open={open} fullWidth>
      <DialogTitle>Modyfikuj grupę</DialogTitle>
      <DialogContent>
        <Stack
          direction="column"
          spacing={2}
          sx={{
            marginTop: 2,
          }}
        >
          <TextField label="Nazwa" value={name} onChange={(e) => setName(e.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <CancelButton onClick={onCancel} />
        <AsyncLoadingButton onClick={handleSave} disabled={saveDisabled}>
          Zapisz
        </AsyncLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

interface ProductGroupRowProps {
  readonly group: ProductGroup;
  readonly productsForGroup: { productId: string; productName: string }[];
  readonly onUpdate: (name: string) => Promise<ProductGroup>;
  readonly onDelete: (force: boolean) => Promise<void>;
}

const ProductGroupRow: FC<ProductGroupRowProps> = ({ group, productsForGroup, onUpdate, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }} data-testid="product-group-row">
        <TableCell>
          <IconButton
            aria-label="szczegóły grupy"
            size="small"
            onClick={() => setOpen(!open)}
            disabled={productsForGroup.length === 0}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{group.name}</TableCell>
        <TableCell>{productsForGroup.length}</TableCell>
        <TableCell>
          <Stack direction="row" spacing={2}>
            <IconButton size="small" aria-label="Edytuj grupę" onClick={() => setDialogOpen(true)}>
              <EditIcon />
            </IconButton>
            <IconButton onClick={() => setConfirmationDialogOpen(true)} aria-label="Usuń grupę ze słownika">
              <DeleteIcon />
            </IconButton>
          </Stack>
        </TableCell>
      </TableRow>
      <UsedByProducts open={open} products={productsForGroup} colSpan={4} />
      <EditGroupDialog
        open={dialogOpen}
        group={group}
        onChange={async (name) => {
          await onUpdate(name);
          setDialogOpen(false);
        }}
        onCancel={() => setDialogOpen(false)}
      />
      <ConfirmationDialog
        open={confirmationDialogOpen}
        title="Usuń grupę ze słownika"
        content={
          productsForGroup.length > 0
            ? `Czy chcesz usunąć grupę ze słownika i ${productsForGroup.length} produktów?`
            : "Czy chcesz usunąć grupę ze słownika? Nie jest ona używana w żadnym produkcie."
        }
        onConfirm={() => onDelete(productsForGroup.length > 0)}
        onCancel={() => setConfirmationDialogOpen(false)}
      />
    </>
  );
};

const ProductGroupsDictionary = () => {
  const {
    productGroups,
    isLoading: areGroupsLoading,
    addProductGroup,
    updateProductGroup,
    deleteProductGroup,
  } = useProductGroups();
  const { products, isLoading: areProductsLoading } = useProducts();
  const isLoading = areGroupsLoading || areProductsLoading;

  const productsForGroup = groupBy(
    products
      .filter((p) => !!p.productsGroup)
      .map((p) => ({
        groupName: p.productsGroup!,
        productName: p.name,
        productId: p.id,
      })),
    ({ groupName }) => groupName,
  );

  return (
    <DictionaryAccordion title="Grupy produktów" isLoading={isLoading}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell>Nazwa</TableCell>
            <TableCell>Liczba wystąpień</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <AddGroupFooter
          onAdd={async (name) => {
            await addProductGroup(name);
          }}
        />
        <TableBody>
          {productGroups.map((g) => (
            <ProductGroupRow
              key={g.id}
              group={g}
              productsForGroup={productsForGroup[g.name] ?? []}
              onUpdate={(name) => updateProductGroup(g.id, { name })}
              onDelete={(force) => deleteProductGroup(g.id, force)}
            />
          ))}
        </TableBody>
      </Table>
    </DictionaryAccordion>
  );
};

export default ProductGroupsDictionary;
